import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { X, Save, Mail, MessageSquare, Bell, Users, Power } from "lucide-react";
import { useNotificationTemplates, NotificationTemplate, CreateTemplateInput, STANDALONE_TEMPLATE_OWNERS } from "@/hooks/useNotificationTemplates";
import { PlaceholderPicker } from "./PlaceholderPicker";
import { Badge } from "@/components/ui/badge";
import { EmailPreviewPanel } from "./EmailPreviewPanel";

interface NotificationTemplateFormProps {
  template: NotificationTemplate | null;
  onClose: () => void;
}

const HOURS_OPTIONS = [
  { value: "1", label: "1 hour before" },
  { value: "2", label: "2 hours before" },
  { value: "3", label: "3 hours before" },
  { value: "4", label: "4 hours before" },
  { value: "5", label: "5 hours before" },
  { value: "6", label: "6 hours before" },
  { value: "12", label: "12 hours before" },
  { value: "24", label: "1 day before" },
  { value: "48", label: "2 days before" },
  { value: "72", label: "3 days before" },
  { value: "96", label: "4 days before" },
  { value: "120", label: "5 days before" },
  { value: "144", label: "6 days before" },
  { value: "168", label: "7 days before" },
];

const CHANNEL_OPTIONS = [
  { id: "email", label: "Email", icon: Mail, enabled: true },
  { id: "whatsapp", label: "WhatsApp", icon: MessageSquare, enabled: false },
  { id: "in_app", label: "In-App", icon: Bell, enabled: false },
] as const;

export function NotificationTemplateForm({ template, onClose }: NotificationTemplateFormProps) {
  const { createTemplate, updateTemplate, isCreating, isUpdating } = useNotificationTemplates();
  const isEditing = !!template?.id;
  // Standalone templates are driven by their own edge function — timing and
  // channels must stay locked so they can't be pulled back into the reminder engine.
  const isStandalone = isEditing && template?.template_type === "standalone";
  const standaloneOwner = STANDALONE_TEMPLATE_OWNERS[template?.name ?? ""];

  const [formData, setFormData] = useState({
    name: template?.name ?? "",
    subject_template: template?.subject_template ?? "",
    body_template: template?.body_template ?? "",
    hours_before: template?.hours_before?.toString() ?? "24",
    is_active: template?.is_active ?? true,
    channels: template?.channels ?? ["email"],
    whatsapp_message_template: template?.whatsapp_message_template ?? "",
    in_app_title_template: template?.in_app_title_template ?? "",
    in_app_body_template: template?.in_app_body_template ?? "",
    in_app_action_url: template?.in_app_action_url ?? "",
    send_to_admins: template?.send_to_admins ?? false,
    send_time: template?.send_time ?? null,
  });

  const handleChannelToggle = (channelId: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      channels: checked 
        ? [...prev.channels, channelId]
        : prev.channels.filter(c => c !== channelId),
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const isDailyReminder = parseInt(formData.hours_before) >= 24;
    
    const input: CreateTemplateInput = {
      name: formData.name,
      subject_template: formData.subject_template,
      body_template: formData.body_template,
      hours_before: parseInt(formData.hours_before),
      is_active: formData.is_active,
      template_type: isStandalone ? "standalone" : "ticket_reminder",
      channels: formData.channels,
      whatsapp_message_template: formData.whatsapp_message_template || null,
      in_app_title_template: formData.in_app_title_template || null,
      in_app_body_template: formData.in_app_body_template || null,
      in_app_action_url: formData.in_app_action_url || null,
      send_to_admins: formData.send_to_admins,
      send_time: isDailyReminder ? (formData.send_time || "10:00:00") : null,
    };

    if (isEditing) {
      updateTemplate({ id: template.id, ...input }, { onSuccess: onClose });
    } else {
      createTemplate(input, { onSuccess: onClose });
    }
  };

  const insertPlaceholder = (placeholder: string, field: "subject_template" | "body_template" | "whatsapp_message_template" | "in_app_title_template" | "in_app_body_template") => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field] + placeholder,
    }));
  };

  const hasChannel = (channelId: string) => formData.channels.includes(channelId);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{isEditing ? "Edit Template" : "Create Template"}</CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className={`grid gap-4 ${isStandalone ? "grid-cols-1" : "grid-cols-2"}`}>
            <div className="space-y-2">
              <Label htmlFor="name">Template Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., 24 Hour Reminder"
                required
              />
            </div>

            {!isStandalone && (
              <div className="space-y-2">
                <Label htmlFor="hours_before">Send Timing</Label>
                <Select
                  value={formData.hours_before}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, hours_before: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {HOURS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Send Time for Daily Reminders (not used by standalone templates, which own their own cadence) */}
          {!isStandalone && parseInt(formData.hours_before) >= 24 && (
            <div className="space-y-2">
              <Label htmlFor="send_time">Send Time</Label>
              <Input
                type="time"
                id="send_time"
                value={formData.send_time?.slice(0, 5) || "10:00"}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  send_time: e.target.value + ":00"
                }))}
                className="w-40"
              />
              <p className="text-xs text-muted-foreground">
                Time of day when the notification will be sent
              </p>
            </div>
          )}

          {isStandalone && (
            <div className="rounded-md bg-muted/30 border p-3 text-xs text-muted-foreground space-y-1">
              <p className="font-medium text-foreground">
                Managed by {standaloneOwner ?? "its own function"}
              </p>
              <p>
                Timing and channels are owned by that function and can't be edited here — this keeps the
                event-reminder engine from picking this template up. You can still edit the copy below.
              </p>
              {formData.name === "series_interest_digest" && (
                <p>
                  Schedule (cadence, day, hour) is managed in the <strong>Series Interest Digest</strong> settings card on this page.
                </p>
              )}
            </div>
          )}

          {/* Channel Selection — hidden for standalone templates, whose channels are function-owned */}
          {!isStandalone && (
            <div className="space-y-3">
              <Label>Channels</Label>
              <div className="flex flex-wrap gap-3">
                {CHANNEL_OPTIONS.map((channel) => (
                  <div key={channel.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`channel-${channel.id}`}
                      checked={hasChannel(channel.id)}
                      onCheckedChange={(checked) => handleChannelToggle(channel.id, !!checked)}
                      disabled={!channel.enabled}
                    />
                    <Label
                      htmlFor={`channel-${channel.id}`}
                      className={`flex items-center gap-2 font-normal ${!channel.enabled ? 'text-muted-foreground' : ''}`}
                    >
                      <channel.icon className="h-4 w-4" />
                      {channel.label}
                      {!channel.enabled && <Badge variant="outline" className="text-xs">Coming soon</Badge>}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Email Content (always shown if email channel is selected) */}
          {hasChannel("email") && (
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Mail className="h-4 w-4" />
                Email Content
              </div>

              {formData.name === "series_interest_digest" && (
                <div className="rounded-md bg-muted/50 border p-3 text-xs text-muted-foreground space-y-1">
                  <p className="font-medium text-foreground">Digest layout is system-rendered</p>
                  <p>
                    Per-series section cards, event tiles, and unsubscribe links are generated automatically and cannot be edited.
                    You control the voice: subject, greeting headline, intro paragraph, and footer link text.
                  </p>
                  <p>
                    Available variables: <code>{"{{first_name}}"}</code>, <code>{"{{total_new}}"}</code>, <code>{"{{plural}}"}</code> (empty or "s").
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="subject_template">
                    {formData.name === "series_interest_digest" ? "Subject line" : "Subject"}
                  </Label>
                  <PlaceholderPicker onSelect={(p) => insertPlaceholder(p, "subject_template")} />
                </div>
                <Input
                  id="subject_template"
                  value={formData.subject_template}
                  onChange={(e) => setFormData((prev) => ({ ...prev, subject_template: e.target.value }))}
                  placeholder={
                    formData.name === "series_interest_digest"
                      ? "{{total_new}} new event{{plural}} you'll love"
                      : "Reminder: {{event_title}} is coming up!"
                  }
                  required={hasChannel("email")}
                />
              </div>

              {formData.name === "series_interest_digest" ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="in_app_title_template">Greeting headline (H1)</Label>
                    <Input
                      id="in_app_title_template"
                      value={formData.in_app_title_template}
                      onChange={(e) => setFormData((prev) => ({ ...prev, in_app_title_template: e.target.value }))}
                      placeholder="Hi {{first_name}}, {{total_new}} new event{{plural}} you'll love"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="body_template">Intro paragraph</Label>
                    <Input
                      id="body_template"
                      value={formData.body_template}
                      onChange={(e) => setFormData((prev) => ({ ...prev, body_template: e.target.value }))}
                      placeholder="Fresh from the series you follow."
                      required={hasChannel("email")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="in_app_body_template">Footer link text</Label>
                    <Input
                      id="in_app_body_template"
                      value={formData.in_app_body_template}
                      onChange={(e) => setFormData((prev) => ({ ...prev, in_app_body_template: e.target.value }))}
                      placeholder="Manage all your interests"
                    />
                    <p className="text-xs text-muted-foreground">
                      Links to <code>/profile#interests</code>.
                    </p>
                  </div>
                </>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="body_template">Body (HTML)</Label>
                    <PlaceholderPicker onSelect={(p) => insertPlaceholder(p, "body_template")} />
                  </div>
                  <Textarea
                    id="body_template"
                    value={formData.body_template}
                    onChange={(e) => setFormData((prev) => ({ ...prev, body_template: e.target.value }))}
                    placeholder="<h1>Hi {{user_first_name}},</h1><p>...</p>"
                    className="min-h-[200px] font-mono text-sm"
                    required={hasChannel("email")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Use {"{{#key}}...{{/key}}"} for conditional sections that only show when the value exists.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* WhatsApp Content (shown if whatsapp channel is selected) */}
          {hasChannel("whatsapp") && (
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center gap-2 text-sm font-medium">
                <MessageSquare className="h-4 w-4" />
                WhatsApp Content
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="whatsapp_message_template">Message</Label>
                  <PlaceholderPicker onSelect={(p) => insertPlaceholder(p, "whatsapp_message_template")} />
                </div>
                <Textarea
                  id="whatsapp_message_template"
                  value={formData.whatsapp_message_template}
                  onChange={(e) => setFormData((prev) => ({ ...prev, whatsapp_message_template: e.target.value }))}
                  placeholder="Hi {{user_first_name}}! Reminder: {{event_title}} is tomorrow..."
                  className="min-h-[100px]"
                />
                <p className="text-xs text-muted-foreground">
                  Keep it short - WhatsApp messages should be concise.
                </p>
              </div>
            </div>
          )}

          {/* In-App Content (shown if in_app channel is selected) */}
          {hasChannel("in_app") && (
            <div className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Bell className="h-4 w-4" />
                In-App Notification Content
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="in_app_title_template">Title</Label>
                  <PlaceholderPicker onSelect={(p) => insertPlaceholder(p, "in_app_title_template")} />
                </div>
                <Input
                  id="in_app_title_template"
                  value={formData.in_app_title_template}
                  onChange={(e) => setFormData((prev) => ({ ...prev, in_app_title_template: e.target.value }))}
                  placeholder="{{event_title}} is coming up!"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="in_app_body_template">Body</Label>
                  <PlaceholderPicker onSelect={(p) => insertPlaceholder(p, "in_app_body_template")} />
                </div>
                <Textarea
                  id="in_app_body_template"
                  value={formData.in_app_body_template}
                  onChange={(e) => setFormData((prev) => ({ ...prev, in_app_body_template: e.target.value }))}
                  placeholder="Don't forget! Your event starts soon."
                  className="min-h-[80px]"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="in_app_action_url">Action URL (optional)</Label>
                <Input
                  id="in_app_action_url"
                  value={formData.in_app_action_url}
                  onChange={(e) => setFormData((prev) => ({ ...prev, in_app_action_url: e.target.value }))}
                  placeholder="/events/{{event_id}}"
                />
                <p className="text-xs text-muted-foreground">
                  Deep link when user taps the notification.
                </p>
              </div>
            </div>
          )}

          {/* Send to Admins Toggle */}
          {hasChannel("email") && formData.name !== "series_interest_digest" && (
            <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
              <Users className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1">
                <Label htmlFor="send_to_admins" className="text-sm font-medium">Also send to admins</Label>
                <p className="text-xs text-muted-foreground">Send a copy of each email to all admin users</p>
              </div>
              <Switch
                id="send_to_admins"
                checked={formData.send_to_admins}
                onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, send_to_admins: checked }))}
              />
            </div>
          )}

          <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/30">
            <Power className="h-4 w-4 text-muted-foreground" />
            <div className="flex-1">
              <Label htmlFor="is_active" className="text-sm font-medium">Active</Label>
              <p className="text-xs text-muted-foreground">Will send notifications when triggered</p>
            </div>
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_active: checked }))}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating || isUpdating} className="gap-2">
              <Save className="h-4 w-4" />
              {isCreating || isUpdating ? "Saving..." : "Save Template"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
      
      {/* Live Preview Panel */}
      {hasChannel("email") && (
        <EmailPreviewPanel
          templateId={template?.id}
          subjectTemplate={formData.subject_template}
          bodyTemplate={formData.body_template}
        />
      )}
    </div>
  );
}
